/**
 * 🛠️ V2X Three.js 便捷辅助函数 - 增强版快速搭建工具包
 *
 * 👋 小朋友，欢迎来到神奇的3D世界！
 *
 * 这个文件就像是一个"魔法工具箱"🧰，里面有很多神奇的工具，
 * 可以帮助你快速创建一个美丽的3D世界，就像用乐高积木搭房子一样简单！
 *
 * 🌟 这个工具箱里有什么？
 * 1. 📦 模型加载器 - 把3D模型文件变成真实的3D物体
 * 2. 🎥 相机控制器 - 让你可以转动视角，就像用手机拍照一样
 * 3. 💡 灯光系统 - 给你的3D世界点亮明亮的灯光
 * 4. 🌍 环境系统 - 给你的世界添加美丽的天空和背景
 * 5. 📊 进度条 - 告诉你加载进度，就像下载游戏时的进度条
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

import {
  V2XCameraControls,
  CameraControlMode,
} from '../controls/camera-controls';

/**
 * 🎨 内置加载界面管理器 - 原生DOM加载效果
 *
 * 这个类负责在quickSetup加载过程中显示专业的加载界面，
 * 无需React/Vue组件，直接使用原生DOM + CSS实现。
 */
class BuiltinLoadingUI {
  private container: HTMLElement;
  private loadingElement: HTMLElement | null = null;
  private config: NonNullable<QuickSetupConfig['loadingUI']>;

  // 🎨 主题配置
  private static readonly THEMES = {
    blue: {
      primary: 'rgba(102, 126, 234, 0.95)',
      secondary: 'rgba(118, 75, 162, 0.95)',
      accent: 'rgba(52, 152, 219, 0.95)',
      progressStart: '#667eea',
      progressEnd: '#764ba2',
    },
    green: {
      primary: 'rgba(34, 197, 94, 0.95)',
      secondary: 'rgba(22, 163, 74, 0.95)',
      accent: 'rgba(16, 185, 129, 0.95)',
      progressStart: '#22c55e',
      progressEnd: '#16a34a',
    },
    purple: {
      primary: 'rgba(147, 51, 234, 0.95)',
      secondary: 'rgba(126, 34, 206, 0.95)',
      accent: 'rgba(168, 85, 247, 0.95)',
      progressStart: '#9333ea',
      progressEnd: '#7e22ce',
    },
    orange: {
      primary: 'rgba(249, 115, 22, 0.95)',
      secondary: 'rgba(234, 88, 12, 0.95)',
      accent: 'rgba(251, 146, 60, 0.95)',
      progressStart: '#f97316',
      progressEnd: '#ea580c',
    },
    dark: {
      primary: 'rgba(30, 41, 59, 0.95)',
      secondary: 'rgba(51, 65, 85, 0.95)',
      accent: 'rgba(71, 85, 105, 0.95)',
      progressStart: '#1e293b',
      progressEnd: '#334155',
    },
  };

  // 📝 默认提示信息
  private static readonly DEFAULT_TIPS = [
    '💡 加载完成后可使用鼠标拖拽旋转视角',
    '🔍 使用滚轮进行缩放操作',
    '⌨️ 按F键切换全屏模式',
    '🎯 按R键重置相机视角',
    '⌨️ 按ESC键退出当前模式',
  ];

  constructor(
    container: HTMLElement,
    config: NonNullable<QuickSetupConfig['loadingUI']>
  ) {
    this.container = container;
    this.config = {
      theme: 'blue',
      title: 'V2X Three.js',
      subtitle: '智能交通三维可视化平台',
      logo: '🛣️',
      showRoadAnimation: true,
      showTips: true,
      backgroundOpacity: 0.95,
      ...config,
    };
  }

  /**
   * 🎨 创建加载界面DOM
   */
  show(): void {
    if (this.loadingElement) return;

    const theme = BuiltinLoadingUI.THEMES[this.config.theme!];
    const tips = this.config.customTips || BuiltinLoadingUI.DEFAULT_TIPS;

    this.loadingElement = document.createElement('div');
    this.loadingElement.className = `v2x-builtin-loading ${this.config.className || ''}`;

    this.loadingElement.innerHTML = `
      <div class="v2x-loading-backdrop"></div>
      <div class="v2x-loading-content">
        <div class="v2x-loading-header">
          <div class="v2x-loading-logo">
            <span class="v2x-logo-icon">${this.config.logo}</span>
            <span class="v2x-logo-text">${this.config.title}</span>
          </div>
          <div class="v2x-loading-subtitle">${this.config.subtitle}</div>
        </div>
        
        <div class="v2x-loading-main">
          ${this.config.showRoadAnimation ? this.createRoadAnimation() : ''}
          
          <div class="v2x-progress-section">
            <div class="v2x-progress-container">
              <div class="v2x-progress-track">
                <div class="v2x-progress-fill" style="width: 0%"></div>
              </div>
              <div class="v2x-progress-text">
                <span class="v2x-progress-percentage">0%</span>
              </div>
            </div>
            
            <div class="v2x-progress-details">
              <div class="v2x-progress-message">初始化中...</div>
              <div class="v2x-progress-stats">
                <span class="v2x-stage">阶段: 准备中</span>
                <span class="v2x-models">模型: 0/0</span>
              </div>
            </div>
          </div>
          
          ${
            this.config.showTips
              ? `
            <div class="v2x-loading-tips">
              ${tips.map(tip => `<p>${tip}</p>`).join('')}
            </div>
          `
              : ''
          }
        </div>
      </div>
    `;

    // 📌 注入CSS样式
    this.injectStyles(theme);

    // 📌 添加到容器
    this.container.appendChild(this.loadingElement);
  }

  /**
   * 🛣️ 创建道路动画HTML
   */
  private createRoadAnimation(): string {
    return `
      <div class="v2x-road-animation">
        <div class="v2x-road-container">
          <div class="v2x-road-center-line"></div>
          <div class="v2x-road-side-line v2x-road-side-left"></div>
          <div class="v2x-road-side-line v2x-road-side-right"></div>
          <div class="v2x-moving-car">🚗</div>
        </div>
      </div>
    `;
  }

  /**
   * 📊 更新进度
   */
  updateProgress(
    progress: LoadProgressCallback extends (p: infer P) => void ? P : never
  ): void {
    if (!this.loadingElement) return;

    const progressFill = this.loadingElement.querySelector(
      '.v2x-progress-fill'
    ) as HTMLElement;
    const progressPercentage = this.loadingElement.querySelector(
      '.v2x-progress-percentage'
    ) as HTMLElement;
    const progressMessage = this.loadingElement.querySelector(
      '.v2x-progress-message'
    ) as HTMLElement;
    const stageElement = this.loadingElement.querySelector(
      '.v2x-stage'
    ) as HTMLElement;
    const modelsElement = this.loadingElement.querySelector(
      '.v2x-models'
    ) as HTMLElement;

    if (
      progressFill &&
      progressPercentage &&
      progressMessage &&
      stageElement &&
      modelsElement
    ) {
      const theme = BuiltinLoadingUI.THEMES[this.config.theme!];

      progressFill.style.width = `${progress.overallProgress}%`;
      progressFill.style.background = `linear-gradient(90deg, ${theme.progressStart} ${Math.max(0, progress.overallProgress - 20)}%, ${theme.progressEnd} ${progress.overallProgress}%)`;

      progressPercentage.textContent = `${Math.round(progress.overallProgress)}%`;
      progressMessage.textContent = progress.message;
      stageElement.textContent = `阶段: ${progress.stage}`;

      if (progress.totalModels > 0) {
        modelsElement.textContent = `模型: ${progress.currentModel + 1}/${progress.totalModels}`;
      }
    }
  }

  /**
   * ❌ 显示错误界面
   */
  showError(error: Error, context: string, onRetry?: () => void): void {
    if (!this.loadingElement) return;

    this.loadingElement.innerHTML = `
      <div class="v2x-loading-backdrop"></div>
      <div class="v2x-loading-content">
        <div class="v2x-error-header">
          <div class="v2x-error-icon">❌</div>
          <h2 class="v2x-error-title">初始化失败</h2>
        </div>
        
        <div class="v2x-error-content">
          <div class="v2x-error-context">${context}</div>
          <div class="v2x-error-message">${error.message}</div>
          
          <div class="v2x-error-details">
            <details>
              <summary>详细错误信息</summary>
              <pre class="v2x-error-stack">${error.stack || '无详细信息'}</pre>
            </details>
          </div>
          
          <div class="v2x-error-actions">
            ${onRetry ? '<button class="v2x-error-retry-btn">🔄 重试</button>' : ''}
            <button class="v2x-error-close-btn">❌ 关闭</button>
          </div>
        </div>
      </div>
    `;

    // 添加错误样式
    this.injectErrorStyles();

    // 绑定事件
    const retryBtn = this.loadingElement.querySelector(
      '.v2x-error-retry-btn'
    ) as HTMLButtonElement;
    const closeBtn = this.loadingElement.querySelector(
      '.v2x-error-close-btn'
    ) as HTMLButtonElement;

    if (retryBtn && onRetry) {
      retryBtn.addEventListener('click', () => {
        this.hide();
        onRetry();
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hide();
      });
    }
  }

  /**
   * 🗑️ 隐藏加载界面
   */
  hide(): void {
    if (this.loadingElement && this.loadingElement.parentNode) {
      this.loadingElement.parentNode.removeChild(this.loadingElement);
      this.loadingElement = null;
    }

    // 移除注入的样式
    const styleElement = document.getElementById('v2x-builtin-loading-styles');
    if (styleElement) {
      styleElement.remove();
    }

    const errorStyleElement = document.getElementById(
      'v2x-builtin-error-styles'
    );
    if (errorStyleElement) {
      errorStyleElement.remove();
    }
  }

  /**
   * 💉 注入CSS样式
   */
  private injectStyles(theme: any): void {
    // 避免重复注入
    if (document.getElementById('v2x-builtin-loading-styles')) return;

    const style = document.createElement('style');
    style.id = 'v2x-builtin-loading-styles';
    style.textContent = this.generateCSS(theme);
    document.head.appendChild(style);
  }

  /**
   * 💉 注入错误样式
   */
  private injectErrorStyles(): void {
    // 避免重复注入
    if (document.getElementById('v2x-builtin-error-styles')) return;

    const style = document.createElement('style');
    style.id = 'v2x-builtin-error-styles';
    style.textContent = this.generateErrorCSS();
    document.head.appendChild(style);
  }

  /**
   * 🎨 生成CSS样式
   */
  private generateCSS(theme: any): string {
    return `
      .v2x-builtin-loading {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        color: white;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
        overflow: hidden;
      }

      .v2x-loading-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 50%, ${theme.accent} 100%);
        opacity: ${this.config.backgroundOpacity};
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
      }

      .v2x-loading-content {
        position: relative;
        z-index: 10001;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 40px;
        background: rgba(255, 255, 255, 0.08);
        border-radius: 20px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(30px);
        -webkit-backdrop-filter: blur(30px);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        min-width: 500px;
        max-width: 600px;
      }

      .v2x-loading-header {
        text-align: center;
        margin-bottom: 40px;
      }

      .v2x-loading-logo {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        margin-bottom: 16px;
      }

      .v2x-logo-icon {
        font-size: 32px;
        animation: v2x-bounce 2s infinite;
      }

      .v2x-logo-text {
        font-size: 24px;
        font-weight: 700;
        background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
      }

      .v2x-loading-subtitle {
        font-size: 16px;
        opacity: 0.9;
        color: #e2e8f0;
      }

      .v2x-road-animation {
        width: 100%;
        height: 80px;
        position: relative;
        margin-bottom: 30px;
        overflow: hidden;
        border-radius: 10px;
        background: linear-gradient(90deg, #2d3748 0%, #4a5568 100%);
      }

      .v2x-road-container {
        position: relative;
        width: 100%;
        height: 100%;
        background: #1a202c;
        border-radius: 10px;
      }

      .v2x-road-center-line {
        position: absolute;
        top: 50%;
        left: 0;
        width: 100%;
        height: 3px;
        background: repeating-linear-gradient(90deg, #fbbf24 0px, #fbbf24 20px, transparent 20px, transparent 40px);
        transform: translateY(-50%);
        animation: v2x-road-flow 2s linear infinite;
      }

      .v2x-road-side-line {
        position: absolute;
        width: 100%;
        height: 2px;
        background: rgba(255, 255, 255, 0.6);
      }

      .v2x-road-side-left { top: 20%; }
      .v2x-road-side-right { bottom: 20%; }

      .v2x-moving-car {
        position: absolute;
        top: 50%;
        left: -30px;
        font-size: 24px;
        transform: translateY(-50%);
        animation: v2x-car-move 3s linear infinite;
      }

      .v2x-progress-section {
        width: 100%;
        margin-bottom: 20px;
      }

      .v2x-progress-container {
        position: relative;
        margin-bottom: 20px;
      }

      .v2x-progress-track {
        width: 100%;
        height: 8px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 4px;
        overflow: hidden;
      }

      .v2x-progress-fill {
        height: 100%;
        border-radius: 4px;
        transition: width 0.3s ease;
        position: relative;
        overflow: hidden;
      }

      .v2x-progress-fill::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        animation: v2x-shimmer 2s infinite;
      }

      .v2x-progress-text {
        text-align: center;
        margin-top: 8px;
      }

      .v2x-progress-percentage {
        font-size: 24px;
        font-weight: 700;
        color: #ffffff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }

      .v2x-progress-details {
        text-align: center;
      }

      .v2x-progress-message {
        font-size: 16px;
        color: #e2e8f0;
        margin-bottom: 8px;
      }

      .v2x-progress-stats {
        display: flex;
        justify-content: center;
        gap: 20px;
        font-size: 12px;
        color: #a0aec0;
        flex-wrap: wrap;
      }

      .v2x-loading-tips {
        text-align: center;
        font-size: 14px;
        color: #cbd5e0;
      }

      .v2x-loading-tips p {
        margin: 4px 0;
      }

      /* 🎬 动画效果 */
      @keyframes v2x-bounce {
        0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
        40% { transform: translateY(-10px); }
        60% { transform: translateY(-5px); }
      }

      @keyframes v2x-road-flow {
        0% { transform: translateX(0) translateY(-50%); }
        100% { transform: translateX(-40px) translateY(-50%); }
      }

      @keyframes v2x-car-move {
        0% { left: calc(100% + 30px); }
        100% { left: -30px; }
      }

      @keyframes v2x-shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }

      /* 📱 响应式设计 */
      @media (max-width: 768px) {
        .v2x-loading-content {
          min-width: 300px;
          max-width: 400px;
          padding: 30px 20px;
        }

        .v2x-progress-stats {
          flex-direction: column;
          gap: 8px;
        }

        .v2x-logo-text {
          font-size: 20px;
        }

        .v2x-loading-subtitle {
          font-size: 14px;
        }

        .v2x-road-animation {
          height: 60px;
        }

        .v2x-moving-car {
          font-size: 20px;
        }
      }
    `;
  }

  /**
   * 🎨 生成错误CSS样式
   */
  private generateErrorCSS(): string {
    return `
      .v2x-error-header {
        text-align: center;
        margin-bottom: 30px;
      }

      .v2x-error-icon {
        font-size: 48px;
        margin-bottom: 16px;
      }

      .v2x-error-title {
        color: #ef4444;
        font-size: 24px;
        font-weight: 700;
        margin: 0;
      }

      .v2x-error-content {
        text-align: left;
      }

      .v2x-error-context {
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.3);
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 16px;
        font-weight: 600;
        color: #fecaca;
      }

      .v2x-error-message {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 6px;
        padding: 16px;
        margin-bottom: 20px;
        font-family: 'Courier New', monospace;
        color: #ffffff;
        word-break: break-word;
      }

      .v2x-error-details {
        margin-bottom: 24px;
      }

      .v2x-error-details summary {
        cursor: pointer;
        color: #94a3b8;
        font-size: 14px;
        margin-bottom: 8px;
      }

      .v2x-error-stack {
        background: rgba(0, 0, 0, 0.5);
        border-radius: 4px;
        padding: 12px;
        font-size: 12px;
        color: #e2e8f0;
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 200px;
        overflow-y: auto;
        margin: 0;
      }

      .v2x-error-actions {
        display: flex;
        gap: 12px;
        justify-content: center;
      }

      .v2x-error-retry-btn,
      .v2x-error-close-btn {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .v2x-error-retry-btn {
        background: linear-gradient(45deg, #10b981, #059669);
        color: white;
      }

      .v2x-error-retry-btn:hover {
        background: linear-gradient(45deg, #059669, #047857);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
      }

      .v2x-error-close-btn {
        background: linear-gradient(45deg, #6b7280, #4b5563);
        color: white;
      }

      .v2x-error-close-btn:hover {
        background: linear-gradient(45deg, #4b5563, #374151);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
      }

      /* 移动端适配 */
      @media (max-width: 768px) {
        .v2x-error-actions {
          flex-direction: column;
        }

        .v2x-error-retry-btn,
        .v2x-error-close-btn {
          width: 100%;
        }
      }
    `;
  }
}

/**
 * 🎮 模型配置接口
 *
 * 🤔 什么是"模型配置"？
 * 就像告诉电脑："我要在这里放一个玩具车，车头朝这个方向，大小是这样的"
 *
 * 📖 参数说明：
 * - url: 3D模型文件的网址，就像玩具的"地址"
 * - name: 给这个模型起个名字，比如"小汽车"、"房子"
 * - position: 模型放在哪个位置（x轴=左右，y轴=上下，z轴=前后）
 * - rotation: 模型朝哪个方向转（想象转动魔方）
 * - scale: 模型的大小（1=正常大小，2=放大2倍，0.5=缩小一半）
 *
 * 🌰 使用例子：
 * ```typescript
 * const carModel: ModelConfig = {
 *   url: 'https://example.com/car.glb',  // 汽车模型的地址
 *   name: '红色小汽车',                    // 给汽车起名字
 *   position: new THREE.Vector3(0, 0, 0), // 放在世界中心
 *   scale: new THREE.Vector3(2, 2, 2)     // 放大2倍
 * };
 * ```
 */
export interface ModelConfig {
  /** 🌐 模型文件的网址，就像玩具的"地址" */
  url: string;
  /** 🏷️ 给模型起个好听的名字，比如"飞机"、"城堡" */
  name?: string;
  /** 📍 模型在3D世界中的位置（x=左右，y=上下，z=前后） */
  position?: THREE.Vector3;
  /** 🌐 地理坐标位置（纬度、经度、海拔） */
  geographicPosition?: {
    latitude: number;
    longitude: number;
    altitude?: number;
  };
  /** 🔄 模型的旋转角度，决定它朝哪个方向 */
  rotation?: THREE.Euler;
  /** 📏 模型的大小（1=正常，2=放大2倍，0.5=缩小一半） */
  scale?: THREE.Vector3;
}

/**
 * 🎯 加载进度回调接口
 *
 * 🤔 什么是"进度回调"？
 * 就像下载游戏时，屏幕上显示"已下载50%"的那个进度条！
 * 当电脑在加载3D模型时，这个函数会告诉你现在进行到哪一步了。
 *
 * 📊 进度信息包含：
 * - stage: 现在在做什么（加载模型？加载环境？）
 * - currentModel: 正在加载第几个模型
 * - totalModels: 总共有多少个模型要加载
 * - itemProgress: 当前任务的进度（0-100）
 * - overallProgress: 总体进度（0-100）
 * - message: 给用户看的友好提示消息
 * - timestamp: 记录时间，知道加载用了多久
 *
 * 🌰 使用例子：
 * ```typescript
 * const showProgress: LoadProgressCallback = (progress) => {
 *   console.log(`加载进度：${progress.overallProgress}%`);
 *   console.log(`正在：${progress.message}`);
 * };
 * ```
 */
export interface LoadProgressCallback {
  (progress: {
    /** 🎬 当前阶段：'models'=加载模型，'environment'=加载环境，'complete'=完成 */
    stage: 'models' | 'environment' | 'complete';
    /** 🔢 正在加载第几个模型（从0开始计数） */
    currentModel: number;
    /** 📊 总共有多少个模型需要加载 */
    totalModels: number;
    /** 📈 当前项目的加载进度（0-100） */
    itemProgress: number;
    /** 🎯 总体加载进度（0-100） */
    overallProgress: number;
    /** 💬 给用户看的友好消息，比如"正在加载小汽车..." */
    message: string;
    /** ⏰ 记录当前时间，用来计算加载速度 */
    timestamp: number;
  }): void;
}

/**
 * 🎛️ 快速设置配置接口
 *
 * 🤔 什么是"快速设置配置"？
 * 就像游戏的设置菜单，你可以选择画质高低、声音大小等。
 * 这里你可以设置3D世界的各种参数，比如要不要阴影、背景颜色等。
 *
 * 🎮 可以设置的内容：
 * - container: 3D世界显示在网页的哪个地方
 * - models: 要加载哪些3D模型
 * - environmentUrl: 环境贴图（天空背景）的地址
 * - onProgress: 加载进度的回调函数
 * - enableDraco: 是否启用模型压缩（让文件更小，加载更快）
 * - antialias: 是否开启抗锯齿（让画面更平滑）
 * - shadowMap: 是否显示阴影
 * - autoFitCamera: 是否自动调整相机位置来看全模型
 * - backgroundColor: 背景颜色
 *
 * 🌰 使用例子：
 * ```typescript
 * const config: QuickSetupConfig = {
 *   models: [{ url: 'car.glb', name: '汽车' }],
 *   backgroundColor: 0x87CEEB, // 天蓝色背景
 *   shadowMap: true,           // 显示阴影
 *   onProgress: (p) => console.log(`进度：${p.overallProgress}%`)
 * };
 * ```
 */
export interface QuickSetupConfig {
  /** 🖼️ 3D世界要显示在网页的哪个位置（HTML元素） */
  container?: HTMLElement;
  /** 📦 要加载的3D模型列表，就像购物清单 */
  models?: ModelConfig[];
  /** 🌅 环境贴图的网址，用来设置天空和反射效果 */
  environmentUrl?: string;
  /** 📊 进度回调函数，告诉你加载进度 */
  onProgress?: LoadProgressCallback;
  /** 🗜️ 是否启用Draco压缩（让模型文件更小，加载更快） */
  enableDraco?: boolean;
  /** 📁 Draco解码器的网址（用来解压缩模型） */
  dracoDecoderPath?: string;
  /** ✨ 是否开启抗锯齿（让画面边缘更平滑，不会有锯齿） */
  antialias?: boolean;
  /** 🔍 是否启用透明度（背景可以透明） */
  alpha?: boolean;
  /** 🌑 是否显示阴影（物体在地面上的影子） */
  shadowMap?: boolean;
  /** 🎯 是否自动调整相机位置（让相机能看到完整的模型） */
  autoFitCamera?: boolean;
  /** 🎮 是否启用相机控制（可以用鼠标拖拽、缩放） */
  enableControls?: boolean;
  /** 🎥 相机控制模式: 'orbit' (轨道) 或 'pan' (平移) */
  cameraControlMode?: CameraControlMode;
  /** 🎨 背景颜色（用16进制颜色代码，比如0xff0000是红色） */
  backgroundColor?: number;
  /** 💡 环境光强度（数字越大越亮，1.0是正常亮度） */
  environmentIntensity?: number;
  /** 🌝 环境光亮度（影响整体亮度，0.3是默认值） */
  ambientLightIntensity?: number;
  /** 🌞 平行光亮度（影响主要光照，1.0是默认值） */
  directionalLightIntensity?: number;
  /** 🌫️ 是否启用雾化效果 */
  enableFog?: boolean;
  /** 🌐 地理坐标系统配置 */
  coordinateSystem?: {
    /** 📍 原点纬度 */
    originLatitude: number;
    /** 📍 原点经度 */
    originLongitude: number;
    /** 📍 原点海拔（米） */
    originAltitude?: number;
    /** 📏 坐标缩放比例 */
    scale?: number;
  };
  /** 🎨 是否启用智能气泡系统 */
  enableBubbleManager?: boolean;
  /** 🛟 是否启用设备管理器 */
  enableDeviceManager?: boolean;
  /** 🚗 是否启用车辆管理器 */
  enableVehicleManager?: boolean;
  /** 🚗 车辆管理器配置 */
  vehicleConfig?: {
    /** 默认车辆模型URL */
    defaultModel?: string;
    /** 模型缩放比例 */
    modelScale?: { x: number; y: number; z: number };
  };
  /** 🎨 内置加载界面配置 */
  loadingUI?: {
    /** 是否启用内置加载界面 */
    enabled?: boolean;
    /** 界面主题 */
    theme?: 'blue' | 'green' | 'purple' | 'orange' | 'dark';
    /** 自定义标题 */
    title?: string;
    /** 自定义副标题 */
    subtitle?: string;
    /** 自定义图标/Logo */
    logo?: string;
    /** 是否显示道路动画 */
    showRoadAnimation?: boolean;
    /** 是否显示操作提示 */
    showTips?: boolean;
    /** 自定义提示文本 */
    customTips?: string[];
    /** 背景透明度 (0-1) */
    backgroundOpacity?: number;
    /** 自定义CSS类名 */
    className?: string;
    /** 错误重试回调 */
    onRetry?: () => void;
  };
}

/**
 * 🎮 加载的模型信息接口
 *
 * 🤔 什么是"加载的模型信息"？
 * 当3D模型成功加载后，电脑会记录这个模型的详细信息，
 * 就像给玩具贴上标签：名字、大小、有多少个零件等。
 *
 * 📋 包含的信息：
 * - name: 模型的名字
 * - group: 模型在3D世界中的"容器"
 * - config: 当初设置的配置信息
 * - stats: 模型的统计数据（有多少个点、三角形等）
 * - meshes: 模型的所有网格（可见的部分）
 * - materials: 模型的所有材质（颜色、质感）
 * - boundingBox: 模型的边界框（知道模型有多大）
 * - loadTime: 加载这个模型用了多长时间
 *
 * 🌰 使用例子：
 * ```typescript
 * console.log(`模型"${modelInfo.name}"加载完成`);
 * console.log(`用时：${modelInfo.loadTime}毫秒`);
 * console.log(`包含${modelInfo.stats.vertices}个顶点`);
 * ```
 */
export interface LoadedModelInfo {
  /** 🏷️ 模型的名字，比如"小汽车"、"城堡" */
  name: string;
  /** 📦 模型在3D世界中的"容器"，包含所有模型数据 */
  group: THREE.Group;
  /** ⚙️ 当初加载时使用的配置信息 */
  config: ModelConfig;
  /** 📊 模型的统计信息（技术数据） */
  stats: {
    /** 🔹 顶点数量（模型由多少个点组成） */
    vertices: number;
    /** 🔺 三角形数量（3D模型都是由小三角形拼成的） */
    triangles: number;
    /** 🎨 材质数量（模型用了多少种颜色和质感） */
    materials: number;
  };
  /** 🧩 模型的所有网格部分（可见的3D物体） */
  meshes: THREE.Mesh[];
  /** 🎭 模型的所有材质（决定颜色、光泽、透明度等） */
  materials: THREE.Material[];
  /** 📏 模型的边界框（告诉我们模型有多大、占多少空间） */
  boundingBox: THREE.Box3;
  /** ⏱️ 加载这个模型花费的时间（毫秒） */
  loadTime: number;
}

/**
 * 🎁 快速设置返回结果接口
 *
 * 🤔 什么是"快速设置返回结果"？
 * 当你成功创建了一个3D世界后，系统会给你一个"工具包"，
 * 里面有操控这个世界需要的所有工具，就像游戏的操作面板。
 *
 * 🎮 工具包里有什么：
 * - scene: 3D场景（整个3D世界）
 * - camera: 相机（你观察世界的"眼睛"）
 * - renderer: 渲染器（把3D世界画到屏幕上的工具）
 * - controls: 控制器（用鼠标操作相机的工具）
 * - models: 所有加载成功的模型列表
 * - animate: 开始动画的函数
 * - destroy: 清理资源的函数（关闭3D世界时用）
 *
 * 🌰 使用例子：
 * ```typescript
 * const { scene, camera, animate, destroy } = await quickSetup(config);
 * animate(); // 开始显示3D世界
 * // ... 使用一段时间后 ...
 * destroy(); // 关闭3D世界，释放内存
 * ```
 */
export interface QuickSetupResult {
  /** 🌍 3D场景，包含所有3D物体、灯光、模型的"世界" */
  scene: THREE.Scene;
  /** 📹 相机，决定从哪个角度观察3D世界 */
  camera: THREE.PerspectiveCamera;
  /** 🖼️ 渲染器，负责把3D世界画到屏幕上 */
  renderer: THREE.WebGLRenderer;
  /** 🎮 控制器，让你可以用鼠标拖拽、缩放视角 */
  controls?: V2XCameraControls;
  /** 📦 所有成功加载的3D模型列表 */
  models: LoadedModelInfo[];
  /** 🌐 地理坐标系统实例（如果启用） */
  coordinateSystem?: import('./CoordinateSystem').CoordinateSystem;
  /** 🎨 气泡管理器实例（如果启用） */
  bubbleManager?: import('./BubbleManager').BubbleManager;
  /** 🛟 设备管理器实例（如果启用） */
  deviceManager?: import('./DeviceManager').DeviceManager;
  /** 🚗 车辆管理器实例（如果启用） */
  vehicleManager?: any;
  /** ▶️ 开始动画循环的函数，让3D世界"动起来" */
  animate: () => void;
  /** 🗑️ 清理资源的函数，关闭3D世界时调用，防止内存泄漏 */
  destroy: () => void;
  /** 🎨 内置加载界面控制方法（如果启用） */
  loadingUI?: {
    /** 显示加载界面 */
    show: () => void;
    /** 隐藏加载界面 */
    hide: () => void;
    /** 更新进度 */
    updateProgress: (progress: Parameters<LoadProgressCallback>[0]) => void;
  };
}

/**
 * 🚀 增强版快速设置Three.js场景
 *
 * 🎉 这是我们最厉害的魔法函数！
 *
 * 🤔 这个函数能做什么？
 * 就像是一个"3D世界制造机"，你只需要告诉它你想要什么，
 * 它就能为你创建一个完整的3D世界，包括：
 * - 🌍 3D场景空间
 * - 📹 观察用的相机
 * - 💡 照明灯光
 * - 🎮 鼠标控制
 * - 📦 3D模型加载
 * - 🌅 环境背景
 * - 📊 加载进度显示
 *
 * 🎯 这个函数的特殊之处：
 * 1. ⚡ 自动处理复杂的设置，你不需要懂技术细节
 * 2. 📊 提供详细的加载进度，用户不会感到等待无聊
 * 3. 🛡️ 自动处理错误，即使模型加载失败也不会崩溃
 * 4. 🧹 提供清理功能，防止内存泄漏
 * 5. 📱 自动适应屏幕大小
 *
 * 🌰 最简单的使用方法：
 * ```typescript
 * // 1. 准备配置
 * const config = {
 *   models: [
 *     { url: 'https://example.com/car.glb', name: '汽车' }
 *   ],
 *   onProgress: (progress) => {
 *     console.log(`加载进度：${progress.overallProgress}%`);
 *   }
 * };
 *
 * // 2. 创建3D世界
 * const { animate, destroy } = await quickSetup(config);
 *
 * // 3. 开始动画
 * animate();
 *
 * // 4. 当不需要时清理资源
 * // destroy();
 * ```
 *
 * 🎨 更高级的使用方法：
 * ```typescript
 * const config = {
 *   container: document.getElementById('my-3d-viewer'),
 *   models: [
 *     {
 *       url: 'road.glb',
 *       name: '道路',
 *       position: new THREE.Vector3(0, 0, 0),
 *       scale: new THREE.Vector3(2, 2, 2)
 *     },
 *     {
 *       url: 'car.glb',
 *       name: '汽车',
 *       position: new THREE.Vector3(10, 0, 0)
 *     }
 *   ],
 *   environmentUrl: 'https://example.com/sky.hdr',
 *   backgroundColor: 0x87CEEB, // 天蓝色
 *   shadowMap: true,
 *   onProgress: (progress) => {
 *     updateProgressBar(progress.overallProgress);
 *     showMessage(progress.message);
 *   }
 * };
 * ```
 *
 * ⚠️ 重要提醒：
 * - 这是一个异步函数，需要用 await 等待
 * - 记得在不需要时调用 destroy() 清理资源
 * - 进度回调会被多次调用，每次都会更新进度信息
 *
 * @param config 配置参数，告诉函数你想要什么样的3D世界
 * @returns 返回一个工具包，包含操控3D世界的所有工具
 */
export async function quickSetup(
  config: QuickSetupConfig = {}
): Promise<QuickSetupResult> {
  let isDestroyed = false;
  let animationFrameId: number;
  const {
    container = document.body,
    models = [],
    environmentUrl,
    onProgress,
    enableDraco = true,
    dracoDecoderPath = 'https://www.gstatic.com/draco/v1/decoders/',
    antialias = true,
    alpha = false,
    shadowMap = true,
    autoFitCamera = true,
    enableControls = true,
    cameraControlMode,
    backgroundColor = 0x2c3e50,
    environmentIntensity = 1.0,
    ambientLightIntensity = 0.3,
    directionalLightIntensity = 1.0,
    enableFog = false,
    coordinateSystem: coordinateSystemConfig,
    enableBubbleManager,
    enableDeviceManager,
    enableVehicleManager,
    vehicleConfig,
    loadingUI,
  } = config;

  const startTime = performance.now();

  // 🎨 默认启用内置加载界面，除非明确禁用
  const loadingConfig = {
    enabled: true,
    theme: 'blue' as const,
    title: 'V2X Three.js',
    subtitle: '智能交通三维可视化平台',
    logo: '🛣️',
    showRoadAnimation: true,
    showTips: true,
    backgroundOpacity: 0.95,
    ...loadingUI,
  };

  let builtinUI: BuiltinLoadingUI | undefined;
  if (loadingConfig.enabled !== false) {
    builtinUI = new BuiltinLoadingUI(container, loadingConfig);
    builtinUI.show();
  }

  // 📊 增强的进度回调，支持内置UI
  const enhancedOnProgress = (
    progress: Parameters<LoadProgressCallback>[0]
  ) => {
    // 更新内置加载界面
    if (builtinUI) {
      builtinUI.updateProgress(progress);
    }

    // 调用用户定义的进度回调
    onProgress?.(progress);
  };

  // 🛡️ 错误处理函数
  const handleError = (error: Error, context: string) => {
    console.error(`[V2X-Three] ${context}:`, error);

    if (builtinUI) {
      builtinUI.showError(error, context, loadingConfig.onRetry);
    }

    throw error;
  };

  try {
    // 创建场景
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(backgroundColor);

    // 创建相机
    const containerRect = container.getBoundingClientRect();
    const camera = new THREE.PerspectiveCamera(
      75,
      containerRect.width / containerRect.height,
      0.1,
      10000
    );
    camera.position.set(10, 10, 10);

    // 创建渲染器
    const renderer = new THREE.WebGLRenderer({
      antialias,
      alpha,
      powerPreference: 'high-performance',
    });

    renderer.setSize(containerRect.width, containerRect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    if (shadowMap) {
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    container.appendChild(renderer.domElement);

    // 创建控制器
    let controls: V2XCameraControls | undefined;
    if (enableControls) {
      controls = new V2XCameraControls(camera, renderer.domElement, {
        mode: cameraControlMode,
      });
      console.log(
        `[quickSetup] Camera controls enabled (Mode: ${cameraControlMode || 'orbit'})`
      );
    }

    // 加载环境贴图
    if (environmentUrl) {
      try {
        enhancedOnProgress({
          stage: 'environment',
          currentModel: 0,
          totalModels: models.length,
          itemProgress: 0,
          overallProgress: 0,
          message: '正在加载环境贴图...',
          timestamp: performance.now(),
        });

        const rgbeLoader = new RGBELoader();
        const envMap = await new Promise<THREE.Texture>((resolve, reject) => {
          rgbeLoader.load(
            environmentUrl,
            texture => {
              enhancedOnProgress({
                stage: 'environment',
                currentModel: 0,
                totalModels: models.length,
                itemProgress: 100,
                overallProgress: models.length === 0 ? 50 : 10,
                message: '环境贴图加载完成',
                timestamp: performance.now(),
              });
              resolve(texture);
            },
            progress => {
              const itemProgress =
                progress.total > 0
                  ? (progress.loaded / progress.total) * 100
                  : 0;
              enhancedOnProgress({
                stage: 'environment',
                currentModel: 0,
                totalModels: models.length,
                itemProgress,
                overallProgress:
                  (itemProgress / 100) * (models.length === 0 ? 50 : 10),
                message: `环境贴图加载中... (${Math.round(itemProgress)}%)`,
                timestamp: performance.now(),
              });
            },
            reject
          );
        });

        envMap.mapping = THREE.EquirectangularReflectionMapping;
        scene.environment = envMap;
        scene.background = envMap;

        // 应用环境光强度
        if (environmentIntensity !== 1.0) {
          renderer.toneMappingExposure = environmentIntensity;
        }
      } catch (error) {
        console.warn('环境贴图加载失败:', error);
        enhancedOnProgress({
          stage: 'environment',
          currentModel: 0,
          totalModels: models.length,
          itemProgress: 0,
          overallProgress: models.length === 0 ? 50 : 10,
          message: '⚠️ 环境贴图加载失败，使用默认环境',
          timestamp: performance.now(),
        });
      }
    }

    // 创建光照
    const ambientLight = new THREE.AmbientLight(
      0x404040,
      ambientLightIntensity
    );
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(
      0xffffff,
      directionalLightIntensity
    );
    directionalLight.position.set(50, 50, 25);
    if (shadowMap) {
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 4096;
      directionalLight.shadow.mapSize.height = 4096;
      directionalLight.shadow.camera.near = 0.5;
      directionalLight.shadow.camera.far = 200;
      directionalLight.shadow.camera.left = -100;
      directionalLight.shadow.camera.right = 100;
      directionalLight.shadow.camera.top = 100;
      directionalLight.shadow.camera.bottom = -100;
    }
    scene.add(directionalLight);

    // 添加雾化效果
    if (enableFog) {
      scene.fog = new THREE.Fog(backgroundColor, 50, 200);
    }

    // 初始化地理坐标系统
    let coordinateSystem:
      | import('./CoordinateSystem').CoordinateSystem
      | undefined;
    if (coordinateSystemConfig) {
      const { createCoordinateSystem } = await import('./CoordinateSystem');
      coordinateSystem = createCoordinateSystem(
        coordinateSystemConfig.originLatitude,
        coordinateSystemConfig.originLongitude,
        coordinateSystemConfig.originAltitude || 0,
        coordinateSystemConfig.scale || 1
      );

      enhancedOnProgress({
        stage: 'models',
        currentModel: 0,
        totalModels: models.length,
        itemProgress: 0,
        overallProgress: 5,
        message: '地理坐标系统初始化完成',
        timestamp: performance.now(),
      });
    }

    // 初始化气泡管理器
    let bubbleManager: import('./BubbleManager').BubbleManager | undefined;
    if (enableBubbleManager) {
      const { createBubbleManager } = await import('./BubbleManager');
      bubbleManager = createBubbleManager(camera, container);
      console.log('[V2X-Three] 智能气泡管理器初始化完成');
    }

    // 初始化设备管理器
    let deviceManager: import('./DeviceManager').DeviceManager | undefined;
    if (enableDeviceManager) {
      const { createDeviceManager } = await import('./DeviceManager');
      deviceManager = createDeviceManager(
        scene,
        camera,
        container,
        bubbleManager,
        coordinateSystem
      );
      console.log('[V2X-Three] 设备管理器初始化完成');
    }

    // 初始化车辆管理器
    let vehicleManager: any = undefined;
    if (enableVehicleManager) {
      // 动态导入以避免循环依赖
      const VehicleManagerModule = await import('./VehicleManager');
      vehicleManager = VehicleManagerModule.createVehicleManager(
        scene,
        camera,
        container,
        bubbleManager,
        coordinateSystem,
        vehicleConfig?.defaultModel
      );
      console.log(
        '[V2X-Three] 车辆管理器初始化完成，默认模型:',
        vehicleConfig?.defaultModel
      );
    }

    // 初始化模型加载器
    const loader = new GLTFLoader();
    if (enableDraco) {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(dracoDecoderPath);
      loader.setDRACOLoader(dracoLoader);
    }

    // 加载模型
    const loadedModels: LoadedModelInfo[] = [];
    const baseProgress = environmentUrl ? 20 : 0;
    const modelProgressWeight = 80 - baseProgress;

    for (let i = 0; i < models.length; i++) {
      const modelConfig = models[i];
      const modelName = modelConfig.name || `model_${i}`;
      const modelStartTime = performance.now();

      enhancedOnProgress({
        stage: 'models',
        currentModel: i,
        totalModels: models.length,
        itemProgress: 0,
        overallProgress:
          baseProgress + (i / models.length) * modelProgressWeight,
        message: `正在加载模型: ${modelName}...`,
        timestamp: performance.now(),
      });

      try {
        const gltf = await new Promise<any>((resolve, reject) => {
          loader.load(
            modelConfig.url,
            gltf => resolve(gltf),
            progress => {
              const itemProgress =
                progress.total > 0
                  ? (progress.loaded / progress.total) * 100
                  : 0;
              enhancedOnProgress({
                stage: 'models',
                currentModel: i,
                totalModels: models.length,
                itemProgress,
                overallProgress:
                  baseProgress +
                  ((i + itemProgress / 100) / models.length) *
                    modelProgressWeight,
                message: `正在加载模型: ${modelName} (${Math.round(itemProgress)}%)`,
                timestamp: performance.now(),
              });
            },
            error => reject(error)
          );
        });

        // 处理模型
        const modelGroup = new THREE.Group();
        modelGroup.name = modelName;
        modelGroup.add(gltf.scene);

        // 设置模型位置（优先使用地理坐标）
        if (modelConfig.geographicPosition && coordinateSystem) {
          const world3DPosition = coordinateSystem.createVector3FromGeographic(
            modelConfig.geographicPosition
          );
          modelGroup.position.copy(world3DPosition);
        } else if (modelConfig.position) {
          modelGroup.position.copy(modelConfig.position);
        }

        if (modelConfig.rotation)
          modelGroup.rotation.copy(modelConfig.rotation);
        if (modelConfig.scale) modelGroup.scale.copy(modelConfig.scale);

        // 收集统计信息
        const meshes: THREE.Mesh[] = [];
        const materials: THREE.Material[] = [];
        let vertices = 0;
        let triangles = 0;

        modelGroup.traverse(child => {
          if (child instanceof THREE.Mesh) {
            meshes.push(child);

            if (shadowMap) {
              child.castShadow = true;
              child.receiveShadow = true;
            }

            if (child.geometry?.attributes?.position) {
              vertices += child.geometry.attributes.position.count;
              if (child.geometry.index) {
                triangles += child.geometry.index.count / 3;
              } else {
                triangles += child.geometry.attributes.position.count / 3;
              }
            }

            if (child.material) {
              const mats = Array.isArray(child.material)
                ? child.material
                : [child.material];
              materials.push(...mats);
            }
          }
        });

        const boundingBox = new THREE.Box3().setFromObject(modelGroup);
        const loadTime = performance.now() - modelStartTime;

        const modelInfo: LoadedModelInfo = {
          name: modelName,
          group: modelGroup,
          config: modelConfig,
          stats: {
            vertices,
            triangles: Math.round(triangles),
            materials: new Set(materials.map(m => m.uuid)).size,
          },
          meshes,
          materials,
          boundingBox,
          loadTime,
        };

        loadedModels.push(modelInfo);
        scene.add(modelGroup);

        enhancedOnProgress({
          stage: 'models',
          currentModel: i,
          totalModels: models.length,
          itemProgress: 100,
          overallProgress:
            baseProgress + ((i + 1) / models.length) * modelProgressWeight,
          message: `模型加载完成: ${modelName} (${Math.round(loadTime)}ms)`,
          timestamp: performance.now(),
        });
      } catch (error) {
        console.error(`模型加载失败: ${modelName}`, error);
        enhancedOnProgress({
          stage: 'models',
          currentModel: i,
          totalModels: models.length,
          itemProgress: 0,
          overallProgress:
            baseProgress + ((i + 1) / models.length) * modelProgressWeight,
          message: `❌ 模型加载失败: ${modelName}`,
          timestamp: performance.now(),
        });
      }
    }

    // 自动调整相机位置
    if (autoFitCamera && loadedModels.length > 0 && controls) {
      const mainBox = new THREE.Box3();
      loadedModels.forEach(modelInfo => {
        mainBox.union(modelInfo.boundingBox);
      });
      if (!mainBox.isEmpty()) {
        //   controls.fitToBox(mainBox, true);
      }
    }

    const clock = new THREE.Clock();

    // Animation loop
    function animate() {
      if (isDestroyed) {
        return;
      }
      animationFrameId = requestAnimationFrame(animate);

      const delta = clock.getDelta();

      // // Update controls
      if (controls) {
        controls.update(delta);
      }

      // Update managers that need per-frame updates
      if (vehicleManager) {
        vehicleManager.update(delta);
      }

      renderer.render(scene, camera);
    }

    animate();

    // 窗口大小变化处理
    const handleResize = () => {
      const rect = container.getBoundingClientRect();
      camera.aspect = rect.width / rect.height;
      camera.updateProjectionMatrix();
      renderer.setSize(rect.width, rect.height);
    };

    window.addEventListener('resize', handleResize);

    // 销毁函数
    const destroy = () => {
      isDestroyed = true;
      cancelAnimationFrame(animationFrameId);
      console.log('🧹 V2X库开始清理资源...');

      window.removeEventListener('resize', handleResize);
      console.log('✅ 窗口事件监听器已移除');

      // 清理模型资源
      loadedModels.forEach((model, index) => {
        scene.remove(model.group);
        model.meshes.forEach(mesh => {
          mesh.geometry?.dispose();
          const materials = Array.isArray(mesh.material)
            ? mesh.material
            : [mesh.material];
          materials.forEach(mat => mat.dispose());
        });
        console.log(`✅ 模型 ${index + 1} 已清理`);
      });

      // 清理控制器
      if (controls) {
        controls.dispose();
        console.log('✅ 相机控制器已清理');
      }

      // 清理设备管理器
      if (deviceManager) {
        deviceManager.dispose();
        console.log('✅ 设备管理器已清理');
      }

      // 清理车辆管理器
      if (vehicleManager) {
        vehicleManager.dispose();
        console.log('✅ 车辆管理器已清理');
      }

      // 清理气泡管理器
      if (bubbleManager) {
        bubbleManager.dispose();
        console.log('✅ 气泡管理器已清理');
      }

      // 清理Draco加载器
      if (enableDraco && loader.dracoLoader) {
        loader.dracoLoader.dispose();
        console.log('✅ Draco加载器已清理');
      }

      // 清理内置加载界面
      if (builtinUI) {
        builtinUI.hide();
        console.log('✅ 内置加载界面已清理');
      }

      // 清理渲染器
      renderer.dispose();
      console.log('✅ 渲染器已清理');

      // 安全移除DOM元素
      try {
        if (renderer.domElement && renderer.domElement.parentNode) {
          // 检查容器是否仍然存在并包含canvas元素
          if (
            container &&
            container.contains &&
            container.contains(renderer.domElement)
          ) {
            container.removeChild(renderer.domElement);
            console.log('✅ Canvas元素已从容器中移除');
          } else if (renderer.domElement.parentNode) {
            // 如果容器不存在，尝试从父节点移除
            renderer.domElement.parentNode.removeChild(renderer.domElement);
            console.log('✅ Canvas元素已从父节点中移除');
          }
        }
      } catch (error) {
        console.warn('⚠️ 移除Canvas元素时出现问题（可能容器已被清理）:', error);
      }

      console.log('🎉 V2X库资源清理完成');
    };

    // 完成报告
    const totalTime = performance.now() - startTime;
    enhancedOnProgress({
      stage: 'complete',
      currentModel: models.length - 1,
      totalModels: models.length,
      itemProgress: 100,
      overallProgress: 100,
      message: `🎉 所有资源加载完成！耗时 ${Math.round(totalTime)}ms`,
      timestamp: performance.now(),
    });

    // 🎨 延迟隐藏加载界面，让用户看到完成状态
    if (builtinUI) {
      setTimeout(() => {
        builtinUI!.hide();
      }, 1000); // 1秒后隐藏
    }

    return {
      scene,
      camera,
      renderer,
      // controls,
      models: loadedModels,
      coordinateSystem,
      bubbleManager,
      deviceManager,
      vehicleManager,
      animate,
      destroy,
      // 🎨 提供加载界面控制方法
      loadingUI: builtinUI
        ? {
            show: () => builtinUI!.show(),
            hide: () => builtinUI!.hide(),
            updateProgress: progress => builtinUI!.updateProgress(progress),
          }
        : undefined,
    };
  } catch (error) {
    // 🛡️ 全局错误处理
    const errorMsg = error instanceof Error ? error : new Error(String(error));
    console.error('[V2X-Three] QuickSetup initialization failed:', errorMsg);

    // 使用错误处理函数显示错误界面
    handleError(errorMsg, '场景初始化失败');

    // 重新抛出错误，让调用者知道失败了
    throw errorMsg;
  }
}

/**
 * 💡 创建基础光照设置
 *
 * 🤔 什么是"光照"？
 * 就像房间里的电灯一样，3D世界也需要灯光才能看清楚物体。
 * 没有灯光的话，所有东西都是黑乎乎的，什么都看不见！
 *
 * 🔆 这个函数会创建两种灯光：
 * 1. 🌙 环境光（ambient light）- 就像月亮光，轻轻地照亮所有地方
 * 2. ☀️ 方向光（directional light）- 就像太阳光，从一个方向强烈照射
 *
 * 🌰 使用例子：
 * ```typescript
 * const scene = new THREE.Scene();
 * const { ambientLight, directionalLight } = createBasicLighting(scene, 1.5);
 * // 现在场景里有了明亮的灯光！
 * ```
 *
 * 💡 小贴士：
 * - intensity 数值越大，灯光越亮
 * - 1.0 是正常亮度，2.0 是两倍亮度
 * - 0.5 是半亮度，适合营造温馨的氛围
 *
 * @param scene 要添加灯光的3D场景
 * @param intensity 灯光强度，1.0是正常亮度，数字越大越亮
 * @returns 返回创建的两个灯光对象，可以进一步调整
 */
export function createBasicLighting(scene: THREE.Scene, intensity: number = 1) {
  const ambientLight = new THREE.AmbientLight(0x404040, 0.3 * intensity);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, intensity);
  directionalLight.position.set(10, 10, 5);
  directionalLight.castShadow = true;
  scene.add(directionalLight);

  return { ambientLight, directionalLight };
}

/**
 * 🏞️ 创建地面
 *
 * 🤔 什么是"地面"？
 * 就像现实世界的地板一样，3D世界也需要一个地面，
 * 这样模型就有地方"站"，而不是飘在空中。
 *
 * 🎨 这个函数会创建：
 * - 📏 一个正方形的平面作为地面
 * - 🎭 给地面涂上指定的颜色
 * - 🌑 让地面能够接收阴影（其他物体的影子会投射在地面上）
 * - 📐 把地面水平放置（旋转90度让它平躺）
 *
 * 🌰 使用例子：
 * ```typescript
 * // 创建一个灰色的大地面
 * const ground = createGround(200, 0x808080);
 * scene.add(ground);
 *
 * // 创建一个绿色的草地
 * const grassGround = createGround(100, 0x90EE90);
 * scene.add(grassGround);
 * ```
 *
 * 🎨 常用颜色代码：
 * - 0x808080 - 灰色（默认）
 * - 0x90EE90 - 淡绿色（草地）
 * - 0xF4A460 - 沙棕色（沙地）
 * - 0x87CEEB - 天蓝色（水面）
 * - 0xFFFFFF - 白色（雪地）
 *
 * 💡 小贴士：
 * - size 决定地面的大小，数字越大地面越大
 * - 颜色使用16进制代码，可以在网上搜索"颜色代码"
 * - 地面会自动接收其他物体的阴影
 *
 * @param size 地面的边长（米），默认100米x100米
 * @param color 地面颜色，使用16进制颜色代码，默认灰色
 * @returns 返回创建的地面网格对象，可以添加到场景中
 */
export function createGround(size: number = 100, color: number = 0x808080) {
  const geometry = new THREE.PlaneGeometry(size, size);
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.8,
    metalness: 0.1,
  });
  const ground = new THREE.Mesh(geometry, material);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  return ground;
}
